import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {catchError, map} from 'rxjs/operators';
import {HttpErrorHandler} from '../../http.error.handler';
import {JsonApiResponse} from '../../shared/models/JsonApiResponse';
import {Page} from '../../shared/models/Page';
import {DvirReport, InspectionReport} from '../../shared/models/dvir';
import {ShiftReportModel} from '../../shared/models/shift.model';
import {Moment} from 'moment';
import {MultiSelectFilter} from '../../shared/components/multi-select-component';
import {firstValueFrom, Observable} from 'rxjs';
import {DeviceReportModel} from '../../shared/models/device-report.model';
import { Storm } from '../../shared/models/storm.model';

@Injectable({
  providedIn: 'root'
})
export class ReportsService {

  constructor(
    private readonly http: HttpClient,
  ) {
  }

  getDvirReport(
    from: Moment,
    to: Moment,
    vehicleGroupFilter: MultiSelectFilter<number>,
    dvirPass: boolean,
    page: number = 1,
    size: number = 25,
    sort?: string
  ) {
    const params = {
      page: String(page),
      size: String(size),
      sort
    };

    if (from) {
      params['from'] = from.toISOString();
    }

    if (to) {
      params['to'] = to.toISOString();
    }

    if (!!vehicleGroupFilter && !!vehicleGroupFilter.elements) {
      params['vehicleGroupIds'] = vehicleGroupFilter.elements.join(':::');
    }

    if (dvirPass !== undefined && dvirPass !== null) {
      params['dvirPass'] = dvirPass;
    }

    return firstValueFrom(
      this.http
        .get<JsonApiResponse<Page<DvirReport>>>(
      `${environment.services.service}v1/report/dvir`, {params}
        )
        .pipe(catchError(HttpErrorHandler.handleError))
    );
  }

  getInspectionReport(id: number) {
    return firstValueFrom(
      this.http.get<JsonApiResponse<InspectionReport>>(
        `${environment.services.service}v1/report/dvir/${id}`
      ).pipe(catchError(HttpErrorHandler.handleError))
    );
  }

  getDvirPDF(
    vehicleId: number = null,
    from: Moment = null,
    to: Moment = null,
    vehicleGroupFilter: MultiSelectFilter<number> = null,
    dvirPass: boolean = null,
  ) {
    const params = {};
    if (vehicleId) {
      params['vehicleId'] = vehicleId;
    }
    if (from) {
      params['dateFrom'] = from.toISOString();
    }
    if (to) {
      params['dateTo'] = to.toISOString();
    }
    if (!!vehicleGroupFilter && !!vehicleGroupFilter.elements) {
      params['vehicleGroupIds'] = vehicleGroupFilter.elements.join(':::');
    }
    if (dvirPass !== undefined && dvirPass !== null) {
      params['dvirPass'] = dvirPass;
    }

    return firstValueFrom(
      this.http
        .post(
          `${environment.services.service}v1/report/dvir/pdf`,
          {},
          {responseType: 'blob', headers: {Accept: 'application/pdf'}, params}
        )
        .pipe(catchError(HttpErrorHandler.handleError))
    );
  }

  getDvirHtml(
    vehicleId: number = null,
    from: Moment = null,
    to: Moment = null,
    vehicleGroupFilter: MultiSelectFilter<number> = null,
    dvirPass: boolean = null,
  ): Promise<Blob> {
    const params = {};
    if (vehicleId) {
      params['vehicleId'] = vehicleId;
    }
    if (from) {
      params['dateFrom'] = from.toISOString();
    }
    if (to) {
      params['dateTo'] = to.toISOString();
    }
    if (!!vehicleGroupFilter && !!vehicleGroupFilter.elements) {
      params['vehicleGroupIds'] = vehicleGroupFilter.elements.join(':::');
    }
    if (dvirPass !== undefined && dvirPass !== null) {
      params['dvirPass'] = dvirPass;
    }

    return firstValueFrom(
      this.http
        .post(
      `${environment.services.service}v1/report/dvir/html`,
          {},
          {responseType: 'blob', headers: {Accept: 'text/html'}, params}
        )
        .pipe(catchError(HttpErrorHandler.handleError))
    );
  }

  getDvirByShiftPDF(shiftId: number, preTrip: boolean) {
    const params = {};
    if (preTrip !== undefined) {
      params['pre-trip'] = preTrip;
    }
    return firstValueFrom(
      this.http
        .post(
          `${environment.services.service}v1/report/dvir/${shiftId}/pdf`,
          {},
          {responseType: 'blob', headers: {Accept: 'application/pdf'}, params}
        )
        .pipe(catchError(HttpErrorHandler.handleError))
    );
  }

  getDvirByShiftHtml(shiftId: number, preTrip: boolean): Promise<Blob> {
    const params = {};
    if (preTrip !== undefined) {
      params['pre-trip'] = preTrip;
    }
    return firstValueFrom(
      this.http
        .post(
          `${environment.services.service}v1/report/dvir/${shiftId}/html`,
          {},
          {responseType: 'blob', headers: {Accept: 'text/html'}, params}
        )
        .pipe(catchError(HttpErrorHandler.handleError))
    );
  }

  getShiftReport(
    from: Moment,
    to: Moment,
    vehicleGroupFilter: MultiSelectFilter<number>,
    vehicleFilter: MultiSelectFilter<number>,
    driverFilter: MultiSelectFilter<number>,
    page: number = 1,
    size: number = 25,
    sort?: string
  ) {
    const params = {
      page: String(page),
      size: String(size),
      sort
    };

    if (from) {
      params['from'] = from.toISOString();
    }

    if (to) {
      params['to'] = to.toISOString();
    }

    if (vehicleGroupFilter?.elements) {
      params['vehicleGroupIds'] = vehicleGroupFilter.elements.join(':::');
    }
    if (vehicleFilter?.elements) {
      params['vehicleIds'] = vehicleFilter.elements.join(':::');
    }
    if (driverFilter?.elements) {
      params['driverIds'] = driverFilter.elements.join(':::');
    }

    return this.http.get<JsonApiResponse<ShiftReportModel>>(
      `${environment.services.service}v1/report/shift`, {params}
    ).pipe(
      catchError(HttpErrorHandler.handleError),
      map(response => response.data)
    );
  }

  getShiftReportCSV(from: Moment,
                    to: Moment,
                    vehicleGroupFilter: MultiSelectFilter<number>,
                    vehicleFilter: MultiSelectFilter<number>,
                    driverFilter: MultiSelectFilter<number>,) {
    const params = {};

    if (from) {
      params['from'] = from.toISOString();
    }

    if (to) {
      params['to'] = to.toISOString();
    }

    if (vehicleGroupFilter?.elements) {
      params['vehicleGroupIds'] = vehicleGroupFilter.elements.join(':::');
    }
    if (vehicleFilter?.elements) {
      params['vehicleIds'] = vehicleFilter.elements.join(':::');
    }
    if (driverFilter?.elements) {
      params['driverIds'] = driverFilter.elements.join(':::');
    }

    return firstValueFrom(
      this.http.get(
        `${environment.services.service}v1/report/shift/csv`, {params, responseType: 'blob'}
      ).pipe(catchError(HttpErrorHandler.handleError))
    );
  }

  getShiftReportPDF(from: Moment,
                    to: Moment,
                    vehicleGroupFilter: MultiSelectFilter<number>,
                    vehicleFilter: MultiSelectFilter<number>,
                    driverFilter: MultiSelectFilter<number>,
                    hiddenColumns: string[]) {
    const params = {};

    if (from) {
      params['from'] = from.toISOString();
    }

    if (to) {
      params['to'] = to.toISOString();
    }

    if (vehicleGroupFilter?.elements) {
      params['vehicleGroupIds'] = vehicleGroupFilter.elements.join(':::');
    }
    if (vehicleFilter?.elements) {
      params['vehicleIds'] = vehicleFilter.elements.join(':::');
    }
    if (driverFilter?.elements) {
      params['driverIds'] = driverFilter.elements.join(':::');
    }

    params['hiddenColumns'] = hiddenColumns.join(',');

    return firstValueFrom(
      this.http.get(
        `${environment.services.service}v1/report/shift/pdf`, {params, responseType: 'blob'}
      ).pipe(catchError(HttpErrorHandler.handleError))
    );
  }


  getDeviceReport(
    criteria?: {},
    page: number = 1,
    size: number = 25,
    sort?: string
  ): Promise<DeviceReportModel> {
    const params = {
      page: String(page),
      size: String(size),
      sort
    };

    Object.assign(params, criteria);

    return firstValueFrom(
      this.http
        .get<JsonApiResponse<DeviceReportModel>>(
          `${environment.services.service}v1/report/device`, {params}
        )
        .pipe(
          catchError(HttpErrorHandler.handleError),
          map(response => response.data)
        )
    );
  }

  getStormReport(
    from: Moment,
    to: Moment,
    page: number = 1,
    size: number = 25,
    sort?: string,
  ) {
    const params = {
      page: String(page),
      size: String(size),
      sort,
    };

    if (from) {
      params['from'] = from.toISOString();
    }

    if (to) {
      params['to'] = to.toISOString();
    }

    return firstValueFrom(
      this.http
        .get<JsonApiResponse<Page<Storm>>>(
        `${environment.services.service}v1/report/storm`, {params}
        )
        .pipe(
          catchError(HttpErrorHandler.handleError),
          map(response => response.data)
        )
    );
  }
}
